import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { format } from "date-fns";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import axios from "axios";
import TeacherIcon from "./../../assets/images/teacher.png";
import RedSchoolIcon from "./../../assets/images/redschool.png";
import GreenSchoolIcon from "./../../assets/images/greenschool.png";
import api from "services/api";
import Loading from "components/Loading";
import Select2Field from "components/Select2Field";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import Icon from "@mui/material/Icon";
import { useFormik, FormikProvider } from "formik";
import LinearProgress from "@mui/material/LinearProgress";
import { useSearchParams } from "react-router-dom";
import { createLineRenderer } from "utils/lineRenderer";
import { useTranslate } from "utils/i18n";
import { apiurl } from "utils";
import { parseListPayload } from "utils/paginationStats";
import { useRegisterAssistantPage } from "assistant/useRegisterAssistantPage";
import {
  buildMapViewportGrid,
  getEstablishmentMapViewport,
  getProgressiveMapQueryZoom,
  normalizeEstablishmentMapFeatures,
} from "services/establishmentService";
import { buildAssignmentEstablishmentParams } from "services/establishmentFilters";
import useEstablishmentFilterOptions from "services/useEstablishmentFilterOptions";
import {
  getEstablishmentCheckInMarkers,
  getEstablishmentTeacherOptions,
  getViewportTeacherCheckInMarkers,
  isNewTeacherMapApiUnavailable,
  shouldFallbackTeacherMapApi,
} from "services/teacherMapService";
import MapSidePanel from "./MapSidePanel";
import MapFiltersDrawer from "./MapFiltersDrawer";
import MapTimelineBar from "./MapTimelineBar";
import {
  BRIGHT_PALETTE,
  shuffle,
  makeOptions,
  normalizeEstablishment,
  getAttendanceTier,
  bindEstablishmentNameTooltip,
  bindEstablishmentPopupHandlers,
  buildEstablishmentPopup,
  buildTeacherPopup,
  boundsFromTeachers,
  fadeInMarker,
  fitMapToAllEstablishments,
  fitMapToBounds,
  filterTeacherRows,
  fetchTeachersForEstablishments,
  collectTeacherFilterOptions,
  countRadiusBuckets,
  createTeacherDivIcon,
  createClusterDivIcon,
  groupTeachersForCluster,
  groupTeachersForHeatmap,
  exportTeachersToExcel,
  defaultSummaryDates,
  isWithinRadius,
  MAP_CONTEXT,
  fetchTeacherHistory,
  filterHistoryByDateRange,
  groupHistoryByDay,
  buildTeacherRosterOptions,
  fetchAssignedTeachersForEstablishment,
  getAttendDateKey,
  computeTeacherTimelineStats,
  resolveEffectiveViewMode,
  defaultViewModeForContext,
  buildHistoryPopup,
  createTimelineMarkerIcon,
  mapTeacherDegreeOptions,
  mapTeacherContractTypeOptions,
  fetchTeacherAssignmentForMap,
  resolveGeoIdsFromNames,
  resolveGeoChainForEstablishment,
  resolveGeoChainForEstablishmentName,
  offsetCompareCoordinates,
  bindLazyTeacherPopup,
  getViewportCacheKey,
  getViewportSnapshot,
  hasMeaningfulViewportChange,
  setBoundedMapCache,
} from "./mapHelpers";

const DEFAULT_PROVINCE_STORAGE_KEY = "everifpro.map.defaultProvinceId";

const GEO_API = {
  provinces: `${apiurl}Assignment/provinces`,
  departments: (provid) => `${apiurl}Assignment/departments?ProvId=${provid}`,
  subprefectures: (dptid) => `${apiurl}Assignment/subprefectures?DptId=${dptid}`,
  establishments: `${apiurl}Assignment/establishments`,
};

const FULL_WORLD_BOUNDS = L.latLngBounds([[-85, -180], [85, 180]]);

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("tk446655440000")}` };
}

function createEstablishmentIcon({ tier, selected = false }) {
  const width = selected ? 48 : 38;
  const height = selected ? 58 : 46;
  const iconUrl = tier === "high" ? GreenSchoolIcon : RedSchoolIcon;
  const tierClass = tier === "high" ? "map-estab-pin--high" : "map-estab-pin--low";
  const selectedClass = selected ? " map-estab-pin--selected" : "";
  return L.icon({
    iconUrl,
    iconSize: [width, height],
    iconAnchor: [width / 2, height],
    popupAnchor: [0, -height + 10],
    className: `map-estab-pin ${tierClass}${selectedClass}`,
  });
}

export default function NewMapPage() {
  const summaryDefaults = useMemo(() => defaultSummaryDates(), []);
  const [establishments, setEstablishments] = useState([]);
  const [establishmentClusters, setEstablishmentClusters] = useState([]);
  const [establishmentMapSummary, setEstablishmentMapSummary] = useState(null);
  const [mapLoadProgress, setMapLoadProgress] = useState({ loaded: 0, total: 0, totalInScope: 0 });
  const [establishmentMapError, setEstablishmentMapError] = useState("");
  const [rawTeachers, setRawTeachers] = useState([]);
  const [compareTeachers, setCompareTeachers] = useState([]);
  const [selectedEstabId, setSelectedEstabId] = useState("");
  const [areaViewActive, setAreaViewActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingTeachers, setLoadingTeachers] = useState(false);
  const [deepLinkLoading, setDeepLinkLoading] = useState(false);
  const [deepLinkError, setDeepLinkError] = useState("");
  const [toolbarSelectKey, setToolbarSelectKey] = useState(0);
  const [teachersRevealReady, setTeachersRevealReady] = useState(false);
  const [popupTeachersExpanded, setPopupTeachersExpanded] = useState(false);
  const [teacherProfileLookup, setTeacherProfileLookup] = useState(() => new Map());
  const [areaSummaryRows, setAreaSummaryRows] = useState([]);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);
  const [summaryStartDate, setSummaryStartDate] = useState(summaryDefaults.startDate);
  const [summaryEndDate, setSummaryEndDate] = useState(summaryDefaults.endDate);
  const [summaryDays, setSummaryDays] = useState("1");
  const [mapContext, setMapContext] = useState(MAP_CONTEXT.IDLE);
  const [teacherRosterOpts, setTeacherRosterOpts] = useState([]);
  const [selectedTimelineDay, setSelectedTimelineDay] = useState("all");
  const [selectedTeacherMeta, setSelectedTeacherMeta] = useState(null);

  const [provOpts, setProvOpts] = useState([]);
  const [deptOpts, setDeptOpts] = useState([]);
  const [subpOpts, setSubpOpts] = useState([]);

  const { t } = useTranslate();
  const [searchParams] = useSearchParams();

  const mapRef = useRef(null);
  const estabLayerRef = useRef(null);
  const teacherLayerRef = useRef(null);
  const heatLayerRef = useRef(null);
  const estabHeatLayerRef = useRef(null);
  const linesLayerRef = useRef(null);
  const drawLineRef = useRef(null);
  const markerByEstabIdRef = useRef(new Map());
  const clusterMarkerByIdRef = useRef(new Map());
  const colorQueueRef = useRef(shuffle(BRIGHT_PALETTE));
  const selectEstablishmentRef = useRef(async () => {});
  const setSelectSchoolRef = useRef(() => {});
  const establishmentsRef = useRef([]);
  const popupLabelsRef = useRef({});
  const selectionTokenRef = useRef(0);
  const establishmentRequestRef = useRef(0);
  const establishmentAbortRef = useRef(null);
  const teacherViewportAbortRef = useRef(null);
  const mapMoveTimerRef = useRef(null);
  const lastViewportRef = useRef(null);
  const establishmentViewportCacheRef = useRef(new Map());
  const teacherViewportCacheRef = useRef(new Map());
  const geographyReadyRef = useRef(false);
  const loadAreaTeachersRef = useRef(async () => {});
  const areaViewActiveRef = useRef(areaViewActive);
  const mapContextRef = useRef(mapContext);
  const areaAttendDateRef = useRef(summaryDefaults.endDate);
  const mapQueryFiltersRef = useRef({});
  const cancelAnimationsRef = useRef([]);
  const selectedEstabIdRef = useRef("");
  const lastDeepLinkTeacherRef = useRef("");
  const formikSetRef = useRef(() => {});
  const popupToggleHandlersRef = useRef({ onExpand: () => {}, onCollapse: () => {} });
  const sidePanelOpenRef = useRef(sidePanelOpen);
  const filtersDrawerOpenRef = useRef(filtersDrawerOpen);

  sidePanelOpenRef.current = sidePanelOpen;
  filtersDrawerOpenRef.current = filtersDrawerOpen;
  areaViewActiveRef.current = areaViewActive;
  mapContextRef.current = mapContext;

  popupToggleHandlersRef.current = {
    onExpand: () => setPopupTeachersExpanded(true),
    onCollapse: () => setPopupTeachersExpanded(false),
  };

  const formik = useFormik({
    initialValues: {
      province: "",
      department: "",
      subprefecture: "",
      selectschool: "",
      selectTeacher: "",
      startDate: summaryDefaults.startDate,
      endDate: summaryDefaults.endDate,
      radiusMeters: "500",
      gender: "",
      degree: "",
      contractType: "",
      type: "",
      status: "",
      compareDate: "",
      estabHeatLayer: false,
      category: "",
      radiusFilter: "",
      viewMode: "markers",
    },
    onSubmit: async (values) => {
      const rangeStart = values.startDate <= values.endDate ? values.startDate : values.endDate;
      const rangeEnd = values.startDate <= values.endDate ? values.endDate : values.startDate;

      if (values.selectTeacher && values.selectschool) {
        const meta = teacherRosterOpts.find((o) => String(o.id) === String(values.selectTeacher));
        setSelectedTeacherMeta(meta ? { name: meta.name, reg: meta.reg } : null);
        await loadTeacherTimelineRef.current(
          values.selectTeacher,
          values.selectschool,
          rangeStart,
          rangeEnd
        );
      } else if (values.selectschool) {
        setSelectedTeacherMeta(null);
        setSelectedTimelineDay("all");
        setMapContext(MAP_CONTEXT.SCHOOL_OVERVIEW);
        await selectEstablishment(values.selectschool, { openPopup: false, attendDate: values.endDate });
      } else {
        setSelectedTeacherMeta(null);
        setSelectedTimelineDay("all");
        setMapContext(MAP_CONTEXT.AREA_OVERVIEW);
        await loadAreaTeachers(values.endDate);
      }
    },
  });

  const { typeOptions, statusOptions } = useEstablishmentFilterOptions({
    t,
    provinceId: formik.values.province,
    departmentId: formik.values.department,
    subPrefectureId: formik.values.subprefecture,
  });

  areaAttendDateRef.current = formik.values.endDate;

  mapQueryFiltersRef.current = {
    province: formik.values.province,
    department: formik.values.department,
    subprefecture: formik.values.subprefecture,
    type: formik.values.type,
    status: formik.values.status,
  };

  const advancedFilterCount = useMemo(() => {
    let n = 0;
    const v = formik.values;
    const defaultView = defaultViewModeForContext(mapContext);
    if (v.radiusMeters && String(v.radiusMeters) !== "500") n += 1;
    if (v.viewMode && v.viewMode !== defaultView) n += 1;
    if (v.radiusFilter) n += 1;
    if (v.gender?.trim()) n += 1;
    if (v.degree?.trim()) n += 1;
    if (v.contractType?.trim()) n += 1;
    if (v.compareDate?.trim()) n += 1;
    if (v.estabHeatLayer) n += 1;
    return n;
  }, [formik.values, mapContext]);

  const filterCriteria = useMemo(
    () => ({
      gender: formik.values.gender,
      degree: formik.values.degree,
      contractType: formik.values.contractType,
      radiusMeters: formik.values.radiusMeters,
      radiusFilter: formik.values.radiusFilter,
    }),
    [
      formik.values.gender,
      formik.values.degree,
      formik.values.contractType,
      formik.values.radiusMeters,
      formik.values.radiusFilter,
    ]
  );

  const filteredTeachers = useMemo(
    () => filterTeacherRows(rawTeachers, filterCriteria, null, teacherProfileLookup),
    [rawTeachers, filterCriteria, teacherProfileLookup]
  );

  const filteredCompareTeachers = useMemo(
    () => filterTeacherRows(compareTeachers, filterCriteria, null, teacherProfileLookup),
    [compareTeachers, filterCriteria, teacherProfileLookup]
  );

  const timelineDays = useMemo(() => {
    if (mapContext !== MAP_CONTEXT.TEACHER_TIMELINE) return [];
    return groupHistoryByDay(filteredTeachers).map(({ date, count }) => ({ date, count }));
  }, [mapContext, filteredTeachers]);

  const displayTeachers = useMemo(() => {
    if (mapContext === MAP_CONTEXT.TEACHER_TIMELINE) {
      if (selectedTimelineDay === "all") return filteredTeachers;
      return filteredTeachers.filter((row) => getAttendDateKey(row) === selectedTimelineDay);
    }

    const primary = filteredTeachers.map((row) => ({ ...row, _compareSlot: "primary" }));
    if (formik.values.compareDate && filteredCompareTeachers.length) {
      return [
        ...primary,
        ...filteredCompareTeachers.map((row) => ({ ...row, _compareSlot: "compare" })),
      ];
    }
    return primary;
  }, [
    mapContext,
    filteredTeachers,
    filteredCompareTeachers,
    selectedTimelineDay,
    formik.values.compareDate,
  ]);

  const compareActive =
    Boolean(formik.values.compareDate) &&
    mapContext !== MAP_CONTEXT.TEACHER_TIMELINE &&
    (filteredCompareTeachers.length > 0 || loadingTeachers);

  const effectiveViewMode = useMemo(() => {
    const resolved = resolveEffectiveViewMode(formik.values.viewMode, mapContext, filteredTeachers.length);
    if (compareActive && resolved !== "markers") return "markers";
    if (
      mapContext === MAP_CONTEXT.AREA_OVERVIEW &&
      !compareActive &&
      resolved === "markers" &&
      filteredTeachers.length > 400
    ) {
      return "cluster";
    }
    return resolved;
  }, [formik.values.viewMode, mapContext, filteredTeachers.length, compareActive]);

  const timelineStats = useMemo(
    () => computeTeacherTimelineStats(filteredTeachers, formik.values.radiusMeters),
    [filteredTeachers, formik.values.radiusMeters]
  );

  const displayRadiusBuckets = useMemo(
    () => countRadiusBuckets(displayTeachers, formik.values.radiusMeters),
    [displayTeachers, formik.values.radiusMeters]
  );

  const teacherFilterOptions = useMemo(() => collectTeacherFilterOptions(rawTeachers), [rawTeachers]);

  const genderOptions = useMemo(
    () =>
      teacherFilterOptions.genders.map((opt) => {
        if (opt.id === "Homme") return { id: opt.id, text: t("gender.male", "Homme") };
        if (opt.id === "Femme") return { id: opt.id, text: t("gender.female", "Femme") };
        return opt;
      }),
    [teacherFilterOptions.genders, t]
  );

  const degreeOptions = useMemo(() => mapTeacherDegreeOptions(), []);

  const contractTypeOptions = useMemo(() => mapTeacherContractTypeOptions(), []);

  const loadTeacherTimelineRef = useRef(async () => {});

  const handleTimelinePrev = useCallback(() => {
    if (selectedTimelineDay === "all") {
      if (timelineDays.length) setSelectedTimelineDay(timelineDays[timelineDays.length - 1].date);
      return;
    }
    const idx = timelineDays.findIndex((d) => d.date === selectedTimelineDay);
    if (idx > 0) setSelectedTimelineDay(timelineDays[idx - 1].date);
    else setSelectedTimelineDay("all");
  }, [selectedTimelineDay, timelineDays]);

  const handleTimelineNext = useCallback(() => {
    if (selectedTimelineDay === "all") {
      if (timelineDays.length) setSelectedTimelineDay(timelineDays[0].date);
      return;
    }
    const idx = timelineDays.findIndex((d) => d.date === selectedTimelineDay);
    if (idx >= 0 && idx < timelineDays.length - 1) setSelectedTimelineDay(timelineDays[idx + 1].date);
  }, [selectedTimelineDay, timelineDays]);

  const radiusBuckets = useMemo(
    () => countRadiusBuckets(filteredTeachers, formik.values.radiusMeters),
    [filteredTeachers, formik.values.radiusMeters]
  );

  useEffect(() => {
    const resolved = resolveEffectiveViewMode(formik.values.viewMode, mapContext, filteredTeachers.length);
    if (resolved !== formik.values.viewMode) {
      formik.setFieldValue("viewMode", resolved);
    }
  }, [mapContext, filteredTeachers.length, formik.values.viewMode, formik]);

  const mappableCount =
    establishmentMapSummary?.mappedInViewport ??
    establishments.filter((e) => e.latitude != null && e.longitude != null).length +
      establishmentClusters.reduce((total, cluster) => total + cluster.count, 0);
  const returnedMapFeatures =
    establishmentMapSummary?.returnedFeatures ?? establishments.length + establishmentClusters.length;
  const checkInRate =
    areaSummaryRows.reduce((s, r) => s + Number(r?.teachersAssigned ?? 0), 0) > 0
      ? Math.round(
          (areaSummaryRows.reduce((s, r) => s + Number(r?.teachersChecked ?? 0), 0) /
            areaSummaryRows.reduce((s, r) => s + Number(r?.teachersAssigned ?? 0), 0)) *
            100
        )
      : 0;

  const assistantPageSnapshot = useMemo(
    () => ({
      pageKey: "teacher-distribution-map",
      route: "/carte-enseignants",
      filters: {
        province: provOpts.find((o) => String(o.id) === String(formik.values.province))?.text,
        department: deptOpts.find((o) => String(o.id) === String(formik.values.department))?.text,
        subprefecture: subpOpts.find((o) => String(o.id) === String(formik.values.subprefecture))?.text,
        establishment: formik.values.selectschool,
        teacher: formik.values.selectTeacher,
        startDate: formik.values.startDate,
        endDate: formik.values.endDate,
        mapContext,
        radiusMeters: formik.values.radiusMeters,
        viewMode: formik.values.viewMode,
        type: formik.values.type,
        status: formik.values.status,
      },
      counts: {
        establishments: mappableCount,
        checkIns: filteredTeachers.length,
        inRadius: radiusBuckets.inRadius,
        outRadius: radiusBuckets.outRadius,
      },
    }),
    [formik.values, provOpts, deptOpts, subpOpts, mappableCount, filteredTeachers.length, radiusBuckets]
  );
  useRegisterAssistantPage(assistantPageSnapshot);

  const popupLabels = {
    noTeachersLabel: t("map.no_teachers", "No teachers found for this date."),
    teachersLabel: t("map.teachers_at_establishment", "Teachers"),
    clickHintLabel: t("map.click_for_teachers", "Click to load teachers."),
    viewProfileLabel: t("map.view_profile", "View Profile"),
    viewMoreLabel: t("map.view_more_teachers", "View {{count}} more"),
    showLessLabel: t("map.show_less_teachers", "Show less"),
    attendanceLabel: t("map.attendance_count", "Attendance count"),
  };
  popupLabelsRef.current = popupLabels;

  const teacherPopupLabels = useMemo(
    () => ({
      viewProfileLabel: t("map.view_profile", "View Profile"),
      viewHistoryLabel: t("map.view_history", "View history"),
      statusLabel: t("home.table.status", "Status"),
      genderLabel: t("home.table.gender", "Gender"),
      degreeLabel: t("home.table.degree", "Degree"),
      distanceLabel: t("map.popup.distance", "Distance"),
      radiusLabel: t("map.popup.establishment", "Establishment"),
      dateLabel: t("map.popup.date", "Date"),
      inRadiusLabel: t("map.kpi.in_radius", "In radius"),
      outRadiusLabel: t("map.kpi.out_radius", "Out of radius"),
      radiusMeters: Number(formik.values.radiusMeters) || 500,
    }),
    [t, formik.values.radiusMeters]
  );

  const getNextColor = () => {
    if (colorQueueRef.current.length === 0) {
      colorQueueRef.current = shuffle(BRIGHT_PALETTE);
    }
    return colorQueueRef.current.shift();
  };

  const clearTeacherOverlay = useCallback(() => {
    cancelAnimationsRef.current.forEach((cancel) => cancel?.());
    cancelAnimationsRef.current = [];
    teacherLayerRef.current?.clearLayers();
    heatLayerRef.current?.clearLayers();
    linesLayerRef.current?.clearLayers();
    mapRef.current?.closePopup();
  }, []);

  const clearLinesAndTeachers = useCallback(() => {
    cancelAnimationsRef.current.forEach((cancel) => cancel?.());
    cancelAnimationsRef.current = [];
    teacherLayerRef.current?.clearLayers();
    heatLayerRef.current?.clearLayers();
    linesLayerRef.current?.clearLayers();
  }, []);

  const resetMarkerHighlights = useCallback(() => {
    const list = establishmentsRef.current;
    markerByEstabIdRef.current.forEach((marker, id) => {
      const estab = list.find((e) => String(e.id) === String(id));
      const tier = getAttendanceTier(estab?.attendanceCount, list);
      marker.setIcon(createEstablishmentIcon({ tier, selected: false }));
      marker.setZIndexOffset(0);
    });
  }, []);

  const setSelectedMarkerHighlight = useCallback((estabId) => {
    const list = establishmentsRef.current;
    markerByEstabIdRef.current.forEach((marker, id) => {
      const estab = list.find((e) => String(e.id) === String(id));
      const tier = getAttendanceTier(estab?.attendanceCount, list);
      const isSelected = String(id) === String(estabId);
      marker.setIcon(createEstablishmentIcon({ tier, selected: isSelected }));
      marker.setZIndexOffset(isSelected ? 1000 : 0);
    });
  }, []);

  const syncEstablishmentMarkerVisibility = useCallback((selectedId, showAllInArea = false) => {
    const layer = estabLayerRef.current;
    if (!layer) return;

    markerByEstabIdRef.current.forEach((marker, id) => {
      const shouldShow = showAllInArea || !selectedId || String(id) === String(selectedId);
      if (shouldShow) {
        if (!layer.hasLayer(marker)) marker.addTo(layer);
        marker.setOpacity(1);
      } else if (layer.hasLayer(marker)) {
        layer.removeLayer(marker);
      }
    });
  }, []);

  const fetchDepartments = useCallback(async (provid) => {
    if (!provid) {
      setDeptOpts([]);
      return [];
    }
    try {
      const { data } = await axios.get(GEO_API.departments(provid), { headers: authHeaders() });
      const opts = makeOptions(data, ["dptid", "id"], ["name", "department"]);
      setDeptOpts(opts);
      return opts;
    } catch (err) {
      console.error("Fetch departments error", err);
      setDeptOpts([]);
      return [];
    }
  }, []);

  const fetchSubprefectures = useCallback(async (dptid) => {
    if (!dptid) {
      setSubpOpts([]);
      return [];
    }
    try {
      const { data } = await axios.get(GEO_API.subprefectures(dptid), { headers: authHeaders() });
      const opts = makeOptions(data, ["sptid", "id"], ["name", "subprefecture"]);
      setSubpOpts(opts);
      return opts;
    } catch (err) {
      console.error("Fetch subprefectures error", err);
      setSubpOpts([]);
      return [];
    }
  }, []);

  const loadEstablishmentList = useCallback(async (sptid, filters = {}) => {
    if (!sptid) return [];
    try {
      const { data } = await axios.get(GEO_API.establishments, {
        params: buildAssignmentEstablishmentParams(sptid, filters),
        headers: authHeaders(),
      });
      return parseListPayload(data).map(normalizeEstablishment).filter((e) => e.id);
    } catch (err) {
      console.error("Load establishments error", err);
      return [];
    }
  }, []);

  const fetchEstablishments = useCallback(
    async (filtersOverride = {}, requestOptions = {}) => {
      const map = mapRef.current;
      if (!map) return null;
      const bounds = requestOptions.boundsOverride || map.getBounds();
      const visualZoom = map.getZoom();
      if (!bounds?.isValid?.() || !Number.isFinite(visualZoom)) return null;
      const zoom = requestOptions.fitEarly
        ? Math.min(visualZoom, 6)
        : getProgressiveMapQueryZoom(visualZoom, 4, 4);

      const requestId = ++establishmentRequestRef.current;
      establishmentAbortRef.current?.abort?.();
      const controller = new AbortController();
      establishmentAbortRef.current = controller;
      const activeFilters = { ...mapQueryFiltersRef.current, ...filtersOverride };
      const cacheKey = getViewportCacheKey(
        {
          zoom: visualZoom,
          north: bounds.getNorth(),
          south: bounds.getSouth(),
          east: bounds.getEast(),
          west: bounds.getWest(),
        },
        activeFilters
      );
      const cached = establishmentViewportCacheRef.current.get(cacheKey);
      if (cached) {
        setEstablishments(cached.establishments);
        setEstablishmentClusters(cached.clusters);
        setEstablishmentMapSummary(cached.summary);
        setMapLoadProgress(cached.progress);
        establishmentsRef.current = cached.establishments;
        if (requestOptions.fitResults) {
          const points = [...cached.establishments, ...cached.clusters].map((item) => [
            item.latitude,
            item.longitude,
          ]);
          if (points.length) {
            fitMapToBounds(map, L.latLngBounds(points), {
              sidePanelOpen: sidePanelOpenRef.current,
              maxZoom: 10,
            });
          }
        }
        setLoading(false);
        return cached.establishments;
      }
      const viewportBounds = {
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest(),
      };
      const cells = requestOptions.fitEarly
        ? [viewportBounds]
        : buildMapViewportGrid(viewportBounds, 4, 4);
      const establishmentById = new Map();
      const clusterById = new Map();
      const combinedSummary = {
        totalInScope: 0,
        totalInViewport: 0,
        mappedInViewport: 0,
        missingCoordinatesInViewport: 0,
        returnedFeatures: 0,
        clustered: false,
      };
      setLoading(true);
      setEstablishmentMapError("");
      setMapLoadProgress({ loaded: 0, total: cells.length, totalInScope: 0 });
      const keepExistingMarkers =
        markerByEstabIdRef.current.size > 0 || clusterMarkerByIdRef.current.size > 0;
      try {
        for (let index = 0; index < cells.length; index += 1) {
          if (controller.signal.aborted || requestId !== establishmentRequestRef.current) return null;
          const response = await getEstablishmentMapViewport(
            {
              ...cells[index],
              zoom,
              provinceId: activeFilters.province,
              departmentId: activeFilters.department,
              subPrefectureId: activeFilters.subprefecture,
              type: activeFilters.type,
              status: activeFilters.status,
            },
            controller.signal
          );
          const normalized = normalizeEstablishmentMapFeatures(response.features);
          normalized.establishments.forEach((establishment) => {
            if (establishment.id) establishmentById.set(String(establishment.id), establishment);
          });
          normalized.clusters.forEach((cluster) => {
            clusterById.set(String(cluster.id), cluster);
          });

          combinedSummary.totalInScope = Math.max(
            combinedSummary.totalInScope,
            Number(response.summary?.totalInScope) || 0
          );
          combinedSummary.totalInViewport += Number(response.summary?.totalInViewport) || 0;
          combinedSummary.mappedInViewport += Number(response.summary?.mappedInViewport) || 0;
          combinedSummary.missingCoordinatesInViewport +=
            Number(response.summary?.missingCoordinatesInViewport) || 0;
          combinedSummary.returnedFeatures = establishmentById.size + clusterById.size;
          combinedSummary.clustered = combinedSummary.clustered || Boolean(response.summary?.clustered);

          if (requestId !== establishmentRequestRef.current) return null;
          const nextEstablishments = Array.from(establishmentById.values());
          const nextClusters = Array.from(clusterById.values());
          if (!keepExistingMarkers) {
            setEstablishments(nextEstablishments);
            setEstablishmentClusters(nextClusters);
            setEstablishmentMapSummary({ ...combinedSummary });
            establishmentsRef.current = nextEstablishments;
          }
          setMapLoadProgress({
            loaded: index + 1,
            total: cells.length,
            totalInScope: combinedSummary.totalInScope,
          });
          if (requestOptions.fitEarly && (establishmentById.size || clusterById.size)) {
            const firstFeature =
              Array.from(clusterById.values())[0] || Array.from(establishmentById.values())[0];
            map.setView(
              [firstFeature.latitude, firstFeature.longitude],
              Math.max(Number(requestOptions.initialZoom) || 8, visualZoom),
              { animate: false }
            );
            return Array.from(establishmentById.values());
          }
        }

        const nextEstablishments = Array.from(establishmentById.values());
        const nextClusters = Array.from(clusterById.values());
        setEstablishments(nextEstablishments);
        setEstablishmentClusters(nextClusters);
        setEstablishmentMapSummary({ ...combinedSummary });
        establishmentsRef.current = nextEstablishments;
        setBoundedMapCache(establishmentViewportCacheRef.current, cacheKey, {
          establishments: nextEstablishments,
          clusters: nextClusters,
          summary: { ...combinedSummary },
          progress: {
            loaded: cells.length,
            total: cells.length,
            totalInScope: combinedSummary.totalInScope,
          },
        });

        if (requestOptions.fitResults) {
          const featurePoints = [
            ...Array.from(establishmentById.values()).map((item) => [item.latitude, item.longitude]),
            ...Array.from(clusterById.values()).map((item) => [item.latitude, item.longitude]),
          ];
          if (featurePoints.length === 1) {
            map.flyTo(featurePoints[0], Math.max(map.getZoom(), 8), { duration: 0.8 });
          } else if (featurePoints.length > 1) {
            fitMapToBounds(map, L.latLngBounds(featurePoints), {
              sidePanelOpen: sidePanelOpenRef.current,
              maxZoom: 10,
            });
          }
        }
        return Array.from(establishmentById.values());
      } catch (err) {
        if (controller.signal.aborted) return null;
        if (requestId !== establishmentRequestRef.current) return null;
        console.error("Map establishments error:", err);
        setEstablishmentMapError(
          err?.response?.data?.message ||
            t("map.establishment_load_error", "Impossible de charger les établissements sur la carte.")
        );
        return null;
      } finally {
        if (requestId === establishmentRequestRef.current) setLoading(false);
      }
    },
    [t]
  );

  const fetchTeachersForEstablishment = useCallback(async (estabId, attendDate = "") => {
    if (!estabId) return [];
    const rangeStart =
      formik.values.startDate <= formik.values.endDate
        ? formik.values.startDate
        : formik.values.endDate;
    const rangeEnd =
      formik.values.startDate <= formik.values.endDate
        ? formik.values.endDate
        : formik.values.startDate;
    const legacyRows = await fetchTeachersForEstablishments(api, [estabId], attendDate, 500, {
      type: formik.values.type,
      status: formik.values.status,
    });
    if (legacyRows.length) return legacyRows;
    try {
      return await getEstablishmentCheckInMarkers(estabId, {
        date: attendDate || undefined,
        startDate: attendDate ? undefined : rangeStart,
        endDate: attendDate ? undefined : rangeEnd,
        gender: formik.values.gender,
        degree: formik.values.degree,
        contractType: formik.values.contractType,
        radiusMeters: formik.values.radiusMeters,
        type: formik.values.type,
        status: formik.values.status,
        pageNumber: 1,
        pageSize: 500,
      });
    } catch (error) {
      if (!shouldFallbackTeacherMapApi(error)) throw error;
      return [];
    }
  }, [
    formik.values.startDate,
    formik.values.endDate,
    formik.values.gender,
    formik.values.degree,
    formik.values.contractType,
    formik.values.radiusMeters,
    formik.values.type,
    formik.values.status,
  ]);

  const loadTeacherRosterOptions = useCallback(
    async ({ search = "", pageNumber = 1, pageSize = 25, signal } = {}) => {
      const estabId = formik.values.selectschool || selectedEstabIdRef.current;
      if (!estabId) return { items: [], pagination: { hasNextPage: false } };
      try {
        return await getEstablishmentTeacherOptions(
          estabId,
          { search, pageNumber, pageSize },
          signal
        );
      } catch (error) {
        if (!isNewTeacherMapApiUnavailable(error)) throw error;
        const assigned = await fetchAssignedTeachersForEstablishment(api, estabId, {
          startDate: formik.values.startDate,
          endDate: formik.values.endDate,
          enrichFromRegistry: false,
        });
        const needle = String(search).trim().toLowerCase();
        const matches = assigned.roster.filter((item) => {
          if (!needle) return true;
          return `${item.text || ""} ${item.name || ""} ${item.reg || ""}`
            .toLowerCase()
            .includes(needle);
        });
        const start = (Number(pageNumber) - 1) * Number(pageSize);
        const items = matches.slice(start, start + Number(pageSize));
        return {
          items,
          pagination: { hasNextPage: start + items.length < matches.length },
        };
      }
    },
    [formik.values.selectschool, formik.values.startDate, formik.values.endDate]
  );

  establishmentsRef.current = establishments;
  selectedEstabIdRef.current = selectedEstabId;

  const updateEstablishmentPopup = useCallback((estabId, teacherRows, isLoading, expanded = false) => {
    const marker = markerByEstabIdRef.current.get(String(estabId));
    const estab = establishmentsRef.current.find((e) => String(e.id) === String(estabId));
    if (!marker || !estab) return;

    const popup = marker.getPopup();
    if (popup) {
      popup.options.className = expanded
        ? "map-custom-popup map-custom-popup--expanded"
        : "map-custom-popup";
    }

    marker.setPopupContent(
      buildEstablishmentPopup(
        estab,
        teacherRows,
        {
          ...popupLabelsRef.current,
          loadingTeachers: isLoading,
          clickHintLabel: isLoading
            ? popupLabelsRef.current.clickHintLabel
            : popupLabelsRef.current.noTeachersLabel,
        },
        expanded
      )
    );

    const popupEl = popup?.getElement?.();
    if (popupEl) {
      popupEl.classList.toggle("map-custom-popup--expanded", expanded);
    }
    bindEstablishmentPopupHandlers(popupEl, popupToggleHandlersRef.current);
  }, []);

  const loadAreaTeachers = useCallback(
    async (
      attendDate,
      filtersOverride = null,
      establishmentsOverride = null,
      { fitResults = true, preserveExisting = false } = {}
    ) => {
      const token = ++selectionTokenRef.current;
      teacherViewportAbortRef.current?.abort?.();
      const teacherController = new AbortController();
      teacherViewportAbortRef.current = teacherController;
      if (!preserveExisting) {
        clearTeacherOverlay();
        setSelectedEstabId("");
        setTeachersRevealReady(false);
        setPopupTeachersExpanded(false);
        setSelectedTimelineDay("all");
        setTeacherRosterOpts([]);
        setTeacherProfileLookup(new Map());
        resetMarkerHighlights();
        syncEstablishmentMarkerVisibility("", true);
        formikSetRef.current("selectschool", "");
        formikSetRef.current("selectTeacher", "");
      }
      setAreaViewActive(true);

      setLoadingTeachers(true);
      const establishmentFilters = filtersOverride || {
        type: formik.values.type,
        status: formik.values.status,
      };
      const sourceEstablishments = establishmentsOverride || establishmentsRef.current;
      const estabIds = sourceEstablishments.map((e) => e.id);
      const map = mapRef.current;
      const bounds = map?.getBounds?.();
      if (!bounds) {
        setLoadingTeachers(false);
        return;
      }

      let list = [];
      let compareList = [];
      try {
        const viewportFilters = {
          north: bounds.getNorth(),
          south: bounds.getSouth(),
          east: bounds.getEast(),
          west: bounds.getWest(),
          zoom: map.getZoom(),
          date: attendDate,
          compareDate: formik.values.compareDate,
          provinceId: formik.values.province,
          departmentId: formik.values.department,
          subPrefectureId: formik.values.subprefecture,
          type: establishmentFilters.type,
          status: establishmentFilters.status,
          gender: formik.values.gender,
          degree: formik.values.degree,
          contractType: formik.values.contractType,
          radiusMeters: formik.values.radiusMeters,
        };
        const cacheFilters = {
          zoom: viewportFilters.zoom,
          date: viewportFilters.date,
          compareDate: viewportFilters.compareDate,
          provinceId: viewportFilters.provinceId,
          departmentId: viewportFilters.departmentId,
          subPrefectureId: viewportFilters.subPrefectureId,
          type: viewportFilters.type,
          status: viewportFilters.status,
          gender: viewportFilters.gender,
          degree: viewportFilters.degree,
          contractType: viewportFilters.contractType,
          radiusMeters: viewportFilters.radiusMeters,
        };
        const cacheKey = getViewportCacheKey(getViewportSnapshot(map), cacheFilters);
        let viewportRows = teacherViewportCacheRef.current.get(cacheKey);
        if (!viewportRows) {
          viewportRows = await getViewportTeacherCheckInMarkers(
            viewportFilters,
            teacherController.signal
          );
          setBoundedMapCache(teacherViewportCacheRef.current, cacheKey, viewportRows);
        }
        list = viewportRows.filter((row) => row._compareSlot !== "compare");
        compareList = viewportRows.filter((row) => row._compareSlot === "compare");
      } catch (error) {
        if (teacherController.signal.aborted) return;
        if (!isNewTeacherMapApiUnavailable(error)) {
          if (token === selectionTokenRef.current) setLoadingTeachers(false);
          throw error;
        }
        if (!estabIds.length) {
          setLoadingTeachers(false);
          return;
        }
        list = await fetchTeachersForEstablishments(
          api,
          estabIds,
          attendDate,
          500,
          establishmentFilters
        );
        if (formik.values.compareDate) {
          compareList = await fetchTeachersForEstablishments(
            api,
            estabIds,
            formik.values.compareDate,
            500,
            establishmentFilters
          );
        }
      }
      if (token !== selectionTokenRef.current) return;
      setLoadingTeachers(false);

      setRawTeachers(list);
      setTeachersRevealReady(true);
      setCompareTeachers(formik.values.compareDate ? compareList : []);

      const teacherBounds = boundsFromTeachers(list, null);
      if (fitResults && map && teacherBounds) {
        fitMapToBounds(map, teacherBounds, {
          sidePanelOpen: sidePanelOpenRef.current,
          maxZoom: 14,
        });
      }
    },
    [
      clearTeacherOverlay,
      resetMarkerHighlights,
      syncEstablishmentMarkerVisibility,
      formik.values.compareDate,
      formik.values.province,
      formik.values.department,
      formik.values.subprefecture,
      formik.values.gender,
      formik.values.degree,
      formik.values.contractType,
      formik.values.radiusMeters,
      formik.values.type,
      formik.values.status,
    ]
  );

  loadAreaTeachersRef.current = loadAreaTeachers;

  const loadTeacherTimeline = useCallback(
    async (teacherId, estabId, startDate, endDate) => {
      const token = ++selectionTokenRef.current;
      const idStr = String(estabId);

      clearTeacherOverlay();
      setAreaViewActive(false);
      setTeachersRevealReady(false);
      setPopupTeachersExpanded(false);
      setSelectedTimelineDay("all");
      setSelectedEstabId(idStr);
      setSelectedMarkerHighlight(idStr);
      syncEstablishmentMarkerVisibility(idStr, false);
      setMapContext(MAP_CONTEXT.TEACHER_TIMELINE);
      formikSetRef.current("viewMode", "lines");

      setLoadingTeachers(true);
      let history = [];
      try {
        history = await fetchTeacherHistory(api, teacherId, {
          startDate,
          endDate,
          pageSize: 500,
        });
      } catch (err) {
        console.error("Teacher history error", err);
      }
      if (token !== selectionTokenRef.current) return;

      let filtered = filterHistoryByDateRange(history, startDate, endDate);
      if (!filtered.length && history.length) {
        filtered = history;
      }
      setLoadingTeachers(false);
      setRawTeachers(filtered);
      setTeachersRevealReady(true);

      const map = mapRef.current;
      const estab = establishmentsRef.current.find((e) => String(e.id) === idStr);
      if (map && estab) {
        const bounds = boundsFromTeachers(filtered, estab);
        if (bounds) {
          fitMapToBounds(map, bounds, {
            sidePanelOpen: sidePanelOpenRef.current,
            maxZoom: 17,
          });
        } else {
          fitMapToBounds(map, L.latLngBounds([[estab.latitude, estab.longitude]]), {
            sidePanelOpen: sidePanelOpenRef.current,
            maxZoom: 16,
          });
        }
      }
    },
    [clearTeacherOverlay, setSelectedMarkerHighlight, syncEstablishmentMarkerVisibility]
  );

  useEffect(() => {
    if (!formik.values.compareDate) return;
    if (mapContext === MAP_CONTEXT.TEACHER_TIMELINE) return;
    if (formik.values.viewMode !== "markers") {
      formik.setFieldValue("viewMode", "markers");
    }
  }, [formik.values.compareDate, mapContext, formik.values.viewMode, formik]);

  const applyTeacherDeepLink = useCallback(
    async (teacherId, estabIdParam, regNumberParam, linkOptions = {}) => {
      setDeepLinkLoading(true);
      setDeepLinkError("");
      try {
        const assignment = await fetchTeacherAssignmentForMap(api, teacherId, regNumberParam);
        let estabId = String(estabIdParam || assignment?.estabId || linkOptions.estabId || "").trim();

        const geoNames = {
          provinceName: linkOptions.provinceName || assignment?.provinceName || "",
          departmentName: linkOptions.departmentName || assignment?.departmentName || "",
          subprefectureName: linkOptions.subPrefectureName || assignment?.subprefectureName || "",
        };

        let geo = null;
        if (geoNames.provinceName) {
          geo = await resolveGeoIdsFromNames(axios, apiurl, authHeaders, geoNames);
        }
        if (!geo?.subprefectureId && estabId) {
          geo = await resolveGeoChainForEstablishment(axios, apiurl, authHeaders, estabId);
        }
        const estabNameHint = linkOptions.estabName || assignment?.estabName || "";
        const estabCodeHint = linkOptions.estabCode || assignment?.estabCode || "";
        if (!geo?.subprefectureId && (estabNameHint || estabCodeHint)) {
          geo = await resolveGeoChainForEstablishmentName(
            axios,
            apiurl,
            authHeaders,
            estabNameHint,
            estabCodeHint
          );
          if (geo?.establishmentId) estabId = geo.establishmentId;
        }
        if (!geo?.subprefectureId) {
          setDeepLinkError(
            t(
              "map.deep_link_failed",
              "Could not locate this teacher's establishment on the map. Select province and school manually."
            )
          );
          return false;
        }

        if (!estabId && geo.establishmentId) estabId = String(geo.establishmentId);
        if (!estabId && geo.establishments?.length && assignment?.estabName) {
          const byName = geo.establishments.find(
            (e) =>
              String(e.name ?? "")
                .trim()
                .toLowerCase() === String(assignment.estabName).trim().toLowerCase()
          );
          if (byName?.id) estabId = String(byName.id);
        }
        if (!estabId) {
          setDeepLinkError(
            t(
              "map.deep_link_failed",
              "Could not locate this teacher's establishment on the map. Select province and school manually."
            )
          );
          return false;
        }

        const deptOptsList = geo.depts?.length
          ? geo.depts
          : await fetchDepartments(geo.provinceId);
        const subpOptsList = geo.subps?.length
          ? geo.subps
          : await fetchSubprefectures(geo.departmentId);
        const estabList = geo.establishments?.length
          ? geo.establishments
          : await loadEstablishmentList(geo.subprefectureId);

        const rangeStart =
          linkOptions.startDate ||
          (formik.values.startDate <= formik.values.endDate
            ? formik.values.startDate
            : formik.values.endDate);
        const rangeEnd =
          linkOptions.endDate ||
          (formik.values.startDate <= formik.values.endDate
            ? formik.values.endDate
            : formik.values.startDate);

        flushSync(() => {
          setProvOpts(geo.provinces || []);
          setDeptOpts(deptOptsList);
          setSubpOpts(subpOptsList);
          setEstablishments(estabList);
          establishmentsRef.current = estabList;
          formik.setValues({
            ...formik.values,
            province: String(geo.provinceId),
            department: String(geo.departmentId),
            subprefecture: String(geo.subprefectureId),
            selectschool: String(estabId),
            selectTeacher: String(teacherId),
            startDate: rangeStart,
            endDate: rangeEnd,
          });
          setToolbarSelectKey((k) => k + 1);
        });

        if (assignment) {
          setSelectedTeacherMeta({ name: assignment.teacherName, reg: assignment.regNumber });
        }

        try {
          const assigned = await fetchAssignedTeachersForEstablishment(api, estabId, {
            estabName: assignment?.estabName ?? "",
            startDate: rangeStart,
            endDate: rangeEnd,
            enrichFromRegistry: false,
          });
          setTeacherProfileLookup(assigned.profiles);
          setTeacherRosterOpts(assigned.roster);
        } catch {
          /* roster optional for deep link */
        }

        await loadTeacherTimelineRef.current(teacherId, estabId, rangeStart, rangeEnd);
        return true;
      } catch (err) {
        console.error("Teacher map deep link error", err);
        setDeepLinkError(
          t(
            "map.deep_link_failed",
            "Could not locate this teacher's establishment on the map. Select province and school manually."
          )
        );
        return false;
      } finally {
        setDeepLinkLoading(false);
      }
    },
    [formik, fetchDepartments, fetchSubprefectures, loadEstablishmentList, t]
  );

  loadTeacherTimelineRef.current = loadTeacherTimeline;

  const selectEstablishment = useCallback(
    async (estabId, { openPopup = true } = {}) => {
      const token = ++selectionTokenRef.current;

      clearTeacherOverlay();
      setRawTeachers([]);
      setTeachersRevealReady(false);
      setPopupTeachersExpanded(false);
      setAreaViewActive(false);
      setSelectedTimelineDay("all");
      setMapContext(estabId ? MAP_CONTEXT.SCHOOL_OVERVIEW : MAP_CONTEXT.IDLE);
      if (estabId) formikSetRef.current("viewMode", "lines");

      if (!estabId) {
        setSelectedEstabId("");
        setTeacherRosterOpts([]);
        setTeacherProfileLookup(new Map());
        resetMarkerHighlights();
        syncEstablishmentMarkerVisibility("", false);
        return;
      }

      const idStr = String(estabId);
      setSelectedEstabId(idStr);
      setSelectedMarkerHighlight(idStr);
      syncEstablishmentMarkerVisibility(idStr, false);
      updateEstablishmentPopup(idStr, [], true);

      const marker = markerByEstabIdRef.current.get(idStr);
      const map = mapRef.current;
      const estab = establishmentsRef.current.find((e) => String(e.id) === idStr);

      if (token !== selectionTokenRef.current) return;
      if (openPopup && marker) marker.openPopup();

      setLoadingTeachers(true);
      const rangeStart =
        formik.values.startDate <= formik.values.endDate
          ? formik.values.startDate
          : formik.values.endDate;
      const rangeEnd =
        formik.values.startDate <= formik.values.endDate
          ? formik.values.endDate
          : formik.values.startDate;

      // Paint lightweight markers first. The full teacher roster is loaded only if the dropdown opens.
      let allCheckIns = [];
      try {
        allCheckIns = await fetchTeachersForEstablishment(idStr, "");
      } catch (error) {
        console.error("Establishment teacher markers error:", error);
      } finally {
        if (token === selectionTokenRef.current) setLoadingTeachers(false);
      }
      if (token !== selectionTokenRef.current) return;

      const list = allCheckIns.some((row) => row?._serverFiltered)
        ? allCheckIns
        : filterHistoryByDateRange(allCheckIns, rangeStart, rangeEnd);
      setTeacherRosterOpts(buildTeacherRosterOptions(allCheckIns));
      setTeacherProfileLookup(new Map());

      if (map && estab) {
        const bounds = boundsFromTeachers(list, estab);
        if (bounds)
          fitMapToBounds(map, bounds, {
            sidePanelOpen: sidePanelOpenRef.current,
            maxZoom: 17,
          });
      }

      if (token !== selectionTokenRef.current) return;

      setRawTeachers(list);
      setTeachersRevealReady(true);
      updateEstablishmentPopup(idStr, list, false);
      if (openPopup && marker) marker.openPopup();

      const compareDate = formik.values.compareDate;
      if (compareDate) {
        let compareList = [];
        try {
          compareList = await fetchTeachersForEstablishment(idStr, compareDate);
        } catch (error) {
          console.error("Comparison teacher markers error:", error);
        }
        if (token !== selectionTokenRef.current) return;
        setCompareTeachers(compareList);
      } else {
        setCompareTeachers([]);
      }
    },
    [fetchTeachersForEstablishment, updateEstablishmentPopup, clearTeacherOverlay, setSelectedMarkerHighlight, resetMarkerHighlights, syncEstablishmentMarkerVisibility, formik.values.endDate, formik.values.startDate, formik.values.compareDate, formik.values.type, formik.values.status]
  );

  selectEstablishmentRef.current = selectEstablishment;
  setSelectSchoolRef.current = (val) => formikSetRef.current("selectschool", val);

  const clearTeacherSelection = useCallback(() => {
    const estabId = selectedEstabIdRef.current || formik.values.selectschool;
    formikSetRef.current("selectTeacher", "");
    setSelectedTeacherMeta(null);
    setSelectedTimelineDay("all");
    if (estabId) {
      selectEstablishment(estabId, { openPopup: false });
    } else {
      setMapContext(MAP_CONTEXT.IDLE);
    }
  }, [selectEstablishment, formik.values.selectschool]);

  const clearMapSelection = useCallback(({ preserveViewport = false } = {}) => {
    selectionTokenRef.current += 1;
    clearTeacherOverlay();
    setSelectedEstabId("");
    setAreaViewActive(false);
    setRawTeachers([]);
    setCompareTeachers([]);
    setTeachersRevealReady(false);
    setPopupTeachersExpanded(false);
    setMapContext(MAP_CONTEXT.IDLE);
    setTeacherRosterOpts([]);
    setTeacherProfileLookup(new Map());
    setSelectedTimelineDay("all");
    setSelectedTeacherMeta(null);
    resetMarkerHighlights();
    syncEstablishmentMarkerVisibility("", false);
    formikSetRef.current("selectschool", "");
    formikSetRef.current("selectTeacher", "");

    const map = mapRef.current;
    if (!preserveViewport && map && establishmentsRef.current.length) {
      fitMapToAllEstablishments(map, establishmentsRef.current, {
        sidePanelOpen: sidePanelOpenRef.current,
      });
    }
  }, [clearTeacherOverlay, resetMarkerHighlights, syncEstablishmentMarkerVisibility]);

  const handleDrawerFilterChange = useCallback(
    (name, value) => {
      formik.setFieldValue(name, value);
    },
    [formik]
  );

  const handleEstablishmentFilterChange = useCallback(
    async (name, value) => {
      const establishmentFilters = {
        type: name === "type" ? value : formik.values.type,
        status: name === "status" ? value : formik.values.status,
      };
      const refreshAreaTeachers = mapContext === MAP_CONTEXT.AREA_OVERVIEW || areaViewActive;

      formik.setFieldValue(name, value);
      formik.setFieldValue("selectschool", "");
      clearMapSelection({ preserveViewport: true });

      const filteredEstablishments = await fetchEstablishments(establishmentFilters);
      if (refreshAreaTeachers && filteredEstablishments) {
        await loadAreaTeachers(
          formik.values.endDate,
          establishmentFilters,
          filteredEstablishments
        );
      }
    },
    [areaViewActive, clearMapSelection, fetchEstablishments, formik, loadAreaTeachers, mapContext]
  );

  const resetAdvancedFilters = useCallback(() => {
    formik.setFieldValue("radiusMeters", "500");
    formik.setFieldValue("viewMode", defaultViewModeForContext(mapContext));
    formik.setFieldValue("radiusFilter", "");
    formik.setFieldValue("gender", "");
    formik.setFieldValue("degree", "");
    formik.setFieldValue("contractType", "");
    formik.setFieldValue("compareDate", "");
    formik.setFieldValue("estabHeatLayer", false);
  }, [formik, mapContext]);

  const loadAreaSummary = useCallback(
    async (event) => {
      event?.preventDefault?.();
      setLoadingSummary(true);
      try {
        const { data } = await api.get("/Teacher/areaattendancesummary", {
          params: {
            startDate: String(summaryStartDate).slice(0, 10),
            endDate: String(summaryEndDate).slice(0, 10),
            days: Number(summaryDays) || 1,
          },
          headers: authHeaders(),
        });
        setAreaSummaryRows(parseListPayload(data));
      } catch (err) {
        console.error("Area summary error", err);
        setAreaSummaryRows([]);
      } finally {
        setLoadingSummary(false);
      }
    },
    [summaryStartDate, summaryEndDate, summaryDays]
  );

  const handleExport = useCallback(() => {
    const stamp = format(new Date(), "yyyy-MM-dd");
    exportTeachersToExcel(
      displayTeachers,
      `teacher-map-${stamp}.xlsx`,
      Number(formik.values.radiusMeters) || 500
    );
  }, [displayTeachers, formik.values.radiusMeters]);

  formikSetRef.current = formik.setFieldValue;

  useEffect(() => {
    const loadProvinceOptions = async () => {
      try {
        const { data: pData } = await axios.get(GEO_API.provinces, { headers: authHeaders() });
        const provinces = makeOptions(pData, ["provid", "id"], ["name", "province"]);
        setProvOpts(provinces);
        if (searchParams.get("teacherId")) {
          geographyReadyRef.current = true;
          return;
        }
        const savedProvince = localStorage.getItem(DEFAULT_PROVINCE_STORAGE_KEY);
        const defaultProvince =
          provinces.find((item) => String(item.id) === String(savedProvince)) || provinces[0];
        if (defaultProvince) {
          const provinceId = String(defaultProvince.id);
          localStorage.setItem(DEFAULT_PROVINCE_STORAGE_KEY, provinceId);
          formikSetRef.current("province", provinceId);
          mapQueryFiltersRef.current = {
            ...mapQueryFiltersRef.current,
            province: provinceId,
            department: "",
            subprefecture: "",
          };
          await fetchDepartments(provinceId);
          geographyReadyRef.current = true;
          await fetchEstablishments(
            { province: provinceId, department: "", subprefecture: "" },
            {
              boundsOverride: FULL_WORLD_BOUNDS,
              fitEarly: true,
              initialZoom: 8,
            }
          );
        } else {
          geographyReadyRef.current = true;
        }
      } catch (err) {
        geographyReadyRef.current = true;
        console.error("Province options error", err);
      }
    };
    loadProvinceOptions();
  }, [fetchDepartments, fetchEstablishments, searchParams]);

  useEffect(() => {
    const teacherId = searchParams.get("teacherId");
    if (!teacherId) {
      lastDeepLinkTeacherRef.current = "";
      return;
    }
    if (lastDeepLinkTeacherRef.current === teacherId) return;

    lastDeepLinkTeacherRef.current = teacherId;
    const estabIdParam = searchParams.get("estabId");
    const regNumberParam = searchParams.get("regNumber") || "";
    applyTeacherDeepLink(teacherId, estabIdParam, regNumberParam, {
      estabId: searchParams.get("estabId") || "",
      estabName: searchParams.get("estabName") || "",
      estabCode: searchParams.get("estabCode") || "",
      provinceName: searchParams.get("provinceName") || "",
      departmentName: searchParams.get("departmentName") || "",
      subPrefectureName: searchParams.get("subPrefectureName") || "",
      startDate: searchParams.get("startDate") || "",
      endDate: searchParams.get("endDate") || "",
    }).then((ok) => {
      if (!ok) lastDeepLinkTeacherRef.current = "";
    });
  }, [searchParams, applyTeacherDeepLink]);

  useEffect(() => {
    if (mapRef.current) return;

    const map = L.map("map", {
      preferCanvas: true,
      zoomAnimation: true,
      fadeAnimation: true,
      markerZoomAnimation: true,
    });
    mapRef.current = map;
    const { drawLine } = createLineRenderer(map);
    drawLineRef.current = drawLine;

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap",
      maxZoom: 19,
    }).addTo(map);

    estabLayerRef.current = L.layerGroup().addTo(map);
    estabHeatLayerRef.current = L.layerGroup().addTo(map);
    teacherLayerRef.current = L.layerGroup().addTo(map);
    heatLayerRef.current = L.layerGroup().addTo(map);
    linesLayerRef.current = L.layerGroup().addTo(map);

    map.setView([15.4, 18.7], 6);

    const onPopupOpen = (e) => {
      bindEstablishmentPopupHandlers(e.popup?.getElement?.(), popupToggleHandlersRef.current);
    };
    const scheduleViewportRefresh = () => {
      if (!geographyReadyRef.current) return;
      const snapshot = getViewportSnapshot(map);
      if (!hasMeaningfulViewportChange(lastViewportRef.current, snapshot)) return;
      lastViewportRef.current = snapshot;
      if (mapMoveTimerRef.current) window.clearTimeout(mapMoveTimerRef.current);
      mapMoveTimerRef.current = window.setTimeout(async () => {
        const establishmentRefresh = fetchEstablishments();
        const shouldRefreshTeachers =
          areaViewActiveRef.current ||
          mapContextRef.current === MAP_CONTEXT.AREA_OVERVIEW;
        const teacherRefresh = shouldRefreshTeachers
          ? loadAreaTeachersRef.current(
              areaAttendDateRef.current,
              null,
              null,
              { fitResults: false, preserveExisting: true }
            ).catch((error) => {
              console.error("Viewport teacher markers error:", error);
            })
          : Promise.resolve();
        await Promise.allSettled([establishmentRefresh, teacherRefresh]);
      }, 600);
    };
    map.on("popupopen", onPopupOpen);
    map.on("moveend", scheduleViewportRefresh);
    scheduleViewportRefresh();

    return () => {
      map.off("popupopen", onPopupOpen);
      map.off("moveend", scheduleViewportRefresh);
      if (mapMoveTimerRef.current) window.clearTimeout(mapMoveTimerRef.current);
      establishmentAbortRef.current?.abort?.();
      teacherViewportAbortRef.current?.abort?.();
      clearTeacherOverlay();
      map.remove();
      mapRef.current = null;
      estabLayerRef.current = null;
      teacherLayerRef.current = null;
      estabHeatLayerRef.current = null;
      heatLayerRef.current = null;
      linesLayerRef.current = null;
      markerByEstabIdRef.current.clear();
      clusterMarkerByIdRef.current.clear();
    };
  }, [clearTeacherOverlay, fetchEstablishments]);

  useEffect(() => {
    const estabLayer = estabLayerRef.current;
    const map = mapRef.current;
    if (!estabLayer || !map) return;

    const labels = popupLabelsRef.current;

    const visibleEstablishmentIds = new Set(establishments.map((item) => String(item.id)));
    markerByEstabIdRef.current.forEach((marker, id) => {
      if (!visibleEstablishmentIds.has(String(id))) {
        estabLayer.removeLayer(marker);
        markerByEstabIdRef.current.delete(id);
      }
    });

    const visibleClusterIds = new Set(establishmentClusters.map((item) => String(item.id)));
    clusterMarkerByIdRef.current.forEach((marker, id) => {
      if (!visibleClusterIds.has(String(id))) {
        estabLayer.removeLayer(marker);
        clusterMarkerByIdRef.current.delete(id);
      }
    });

    establishments.forEach((estab, index) => {
      if (estab.latitude == null || estab.longitude == null) return;
      const tier = getAttendanceTier(estab.attendanceCount, establishments);
      const existingMarker = markerByEstabIdRef.current.get(String(estab.id));
      if (existingMarker) {
        const isSelected = String(estab.id) === String(selectedEstabIdRef.current);
        existingMarker.setIcon(createEstablishmentIcon({ tier, selected: isSelected }));
        existingMarker.setZIndexOffset(isSelected ? 1000 : 0);
        return;
      }

      const marker = L.marker([estab.latitude, estab.longitude], {
        icon: createEstablishmentIcon({ tier, selected: false }),
        opacity: 0,
      }).addTo(estabLayer);

      fadeInMarker(marker, Math.min(index * 8, 480));
      markerByEstabIdRef.current.set(String(estab.id), marker);
      marker.bindPopup(
        buildEstablishmentPopup(estab, [], {
          ...labels,
          loadingTeachers: false,
          noTeachersLabel: t("map.no_teachers", "No teachers found for this date."),
        }),
        { className: "map-custom-popup", maxWidth: 280 }
      );
      bindEstablishmentNameTooltip(marker, estab.name);

      marker.on("click", () => {
        setSelectSchoolRef.current(String(estab.id));
        formikSetRef.current("selectTeacher", "");
        setSelectedTeacherMeta(null);
        setSelectedTimelineDay("all");
        selectEstablishmentRef.current(estab.id, { openPopup: true, attendDate: formik.values.endDate });
      });
    });

    establishmentClusters.forEach((cluster) => {
      const existingMarker = clusterMarkerByIdRef.current.get(String(cluster.id));
      if (existingMarker) {
        existingMarker.setIcon(createClusterDivIcon(cluster.count));
        return;
      }
      const marker = L.marker([cluster.latitude, cluster.longitude], {
        icon: createClusterDivIcon(cluster.count),
      }).addTo(estabLayer);
      marker.bindTooltip(
        t("map.establishment_cluster", "{{count}} établissements").replace(
          "{{count}}",
          String(cluster.count)
        ),
        { direction: "top", className: "map-estab-tooltip" }
      );
      marker.on("click", () => {
        const nextZoom = Math.max(
          map.getZoom() + 2,
          cluster.expansionZoom || map.getZoom() + 2
        );
        map.flyTo([cluster.latitude, cluster.longitude], Math.min(nextZoom, 18));
      });
      clusterMarkerByIdRef.current.set(String(cluster.id), marker);
    });

    if (selectedEstabIdRef.current) {
      syncEstablishmentMarkerVisibility(selectedEstabIdRef.current, areaViewActive);
      setSelectedMarkerHighlight(selectedEstabIdRef.current);
    } else if (areaViewActive) {
      syncEstablishmentMarkerVisibility("", true);
    }

  }, [establishments, establishmentClusters, t, syncEstablishmentMarkerVisibility, setSelectedMarkerHighlight, areaViewActive, formik.values.endDate]);

  useEffect(() => {
    const layer = estabHeatLayerRef.current;
    if (!layer) return;
    layer.clearLayers();

    if (!formik.values.estabHeatLayer || !establishments.length) return;

    const maxCount = Math.max(...establishments.map((e) => Number(e.attendanceCount) || 0), 1);
    establishments.forEach((estab) => {
      if (estab.latitude == null || estab.longitude == null) return;
      const count = Number(estab.attendanceCount) || 0;
      const intensity = count / maxCount;
      L.circleMarker([estab.latitude, estab.longitude], {
        radius: 10 + intensity * 26,
        fillColor: "#f59e0b",
        color: "#b45309",
        weight: 1,
        fillOpacity: 0.22 + intensity * 0.45,
      })
        .bindTooltip(
          `${estab.name || "—"} — ${count} ${t("map.attendance_count", "Attendance count")}`,
          { direction: "top", className: "map-estab-tooltip" }
        )
        .addTo(layer);
    });
  }, [establishments, formik.values.estabHeatLayer, t]);

  useEffect(() => {
    const teacherLayer = teacherLayerRef.current;
    const heatLayer = heatLayerRef.current;
    const linesLayer = linesLayerRef.current;
    const drawLine = drawLineRef.current;
    if (!teacherLayer || !heatLayer || !linesLayer || !drawLine) return;

    clearLinesAndTeachers();

    if (!teachersRevealReady || !displayTeachers.length) return;

    const viewMode = effectiveViewMode;
    const isTeacherTimeline = mapContext === MAP_CONTEXT.TEACHER_TIMELINE;
    const buildPopup = isTeacherTimeline ? buildHistoryPopup : buildTeacherPopup;
    const selectedEstab = establishmentsRef.current.find((e) => String(e.id) === String(selectedEstabId));
    const estabPoint =
      selectedEstab?.latitude != null && selectedEstab?.longitude != null
        ? [selectedEstab.latitude, selectedEstab.longitude]
        : null;

    const personIcon = L.icon({
      iconUrl: TeacherIcon,
      iconSize: [36, 50],
      iconAnchor: [18, 50],
      popupAnchor: [0, -44],
      className: "map-teacher-icon",
    });

    const radiusMeters = Number(formik.values.radiusMeters) || 500;
    let lineIndex = 0;

    const markerCoords = (row) => {
      const attLat = parseFloat(row?.attLatitude ?? row?.AttLatitude);
      const attLng = parseFloat(row?.attLongitude ?? row?.AttLongitude);
      if (!Number.isFinite(attLat) || !Number.isFinite(attLng)) return null;
      if (row?._compareSlot === "compare") {
        return offsetCompareCoordinates(attLat, attLng);
      }
      return [attLat, attLng];
    };

    if (viewMode === "heatmap") {
      const cells = groupTeachersForHeatmap(displayTeachers);
      const maxCount = Math.max(...cells.map((c) => c.count), 1);
      cells.forEach((cell) => {
        const intensity = cell.count / maxCount;
        L.circleMarker([cell.lat, cell.lng], {
          radius: 8 + intensity * 22,
          fillColor: "#667eea",
          color: "#4338ca",
          weight: 1,
          fillOpacity: 0.25 + intensity * 0.45,
        })
          .bindTooltip(`${cell.count} ${t("map.teachers_at_establishment", "Teachers")}`, { direction: "top" })
          .addTo(heatLayer);
      });
      return;
    }

    if (viewMode === "cluster") {
      const groups = groupTeachersForCluster(displayTeachers, 2);
      groups.forEach((group, idx) => {
        if (group.rows.length === 1) {
          const row = group.rows[0];
          const coords = markerCoords(row);
          if (!coords) return;
          const [attLat, attLng] = coords;
          const within = isWithinRadius(row, radiusMeters);
          const isCompare = row?._compareSlot === "compare";
          const kind = isCompare ? "compare" : within === true ? "in" : within === false ? "out" : "default";
          const marker = L.marker([attLat, attLng], { icon: createTeacherDivIcon(kind), opacity: 0 }).addTo(teacherLayer);
          bindLazyTeacherPopup(marker, row, (popupRow) =>
            buildPopup(popupRow, teacherPopupLabels)
          );
          fadeInMarker(marker, idx * 30);
          return;
        }

        const marker = L.marker([group.lat, group.lng], {
          icon: createClusterDivIcon(group.rows.length),
        }).addTo(teacherLayer);
        marker.bindPopup(
          `<div class="map-popup-title">${group.rows.length} ${t("map.teachers_at_establishment", "Teachers")}</div>`,
          { className: "map-custom-popup" }
        );
      });
      return;
    }

    displayTeachers.forEach((row) => {
      const coords = markerCoords(row);
      const estLat = parseFloat(row?.estLatitude ?? row?.EstLatitude);
      const estLng = parseFloat(row?.estLongitude ?? row?.EstLongitude);

      const lineFrom =
        Number.isFinite(estLat) && Number.isFinite(estLng)
          ? [estLat, estLng]
          : estabPoint;
      const lineTo = coords;
      const within = isWithinRadius(row, radiusMeters);
      const isCompare = row?._compareSlot === "compare";
      const kind = isCompare ? "compare" : within === true ? "in" : within === false ? "out" : "default";
      const markerIcon = isTeacherTimeline
        ? createTimelineMarkerIcon(kind, { selected: selectedTimelineDay !== "all", dayIndex: lineIndex + 1 })
        : createTeacherDivIcon(kind);

      const showLines = viewMode === "lines" && lineFrom && lineTo && !isCompare;

      if (showLines) {
        const color = getNextColor();
        const stagger = lineIndex * 80;
        let teacherMarker = null;

        const { cancelAnimation } = drawLineRef.current(lineFrom, lineTo, color, {
          layerGroup: linesLayer,
          animate: true,
          duration: 720,
          delay: stagger,
          onComplete: () => {
            if (!teacherMarker) return;
            fadeInMarker(teacherMarker, 0);
          },
        });
        if (cancelAnimation) cancelAnimationsRef.current.push(cancelAnimation);

        if (coords) {
          const [attLat, attLng] = coords;
          teacherMarker = L.marker([attLat, attLng], {
            icon: isTeacherTimeline || viewMode === "markers" ? markerIcon : personIcon,
            opacity: 0,
          }).addTo(teacherLayer);
          bindLazyTeacherPopup(teacherMarker, row, (popupRow) =>
            buildPopup(popupRow, teacherPopupLabels)
          );
        }
        lineIndex += 1;
      } else if (coords) {
        const [attLat, attLng] = coords;
        const marker = L.marker([attLat, attLng], {
          icon: markerIcon,
          opacity: 0,
        }).addTo(teacherLayer);
        bindLazyTeacherPopup(marker, row, (popupRow) =>
          buildPopup(popupRow, teacherPopupLabels)
        );
        fadeInMarker(marker, lineIndex * 40);
        lineIndex += 1;
      }
    });
  }, [
    displayTeachers,
    mapContext,
    selectedEstabId,
    selectedTimelineDay,
    teachersRevealReady,
    clearLinesAndTeachers,
    effectiveViewMode,
    formik.values.radiusMeters,
    teacherPopupLabels,
    t,
  ]);

  useEffect(() => {
    if (!selectedEstabId) return;
    updateEstablishmentPopup(selectedEstabId, filteredTeachers, loadingTeachers, popupTeachersExpanded);
    const marker = markerByEstabIdRef.current.get(String(selectedEstabId));
    if (marker?.isPopupOpen?.()) {
      marker.openPopup();
    }
  }, [selectedEstabId, filteredTeachers, loadingTeachers, popupTeachersExpanded, updateEstablishmentPopup]);

  const filterField = (
    label,
    name,
    options,
    value,
    onChange,
    disabled = false,
    wide = false,
    allowClear = false,
    showAvatars = false,
    loadOptions = null
  ) => (
    <div className={`map-toolbar-field${wide ? " map-toolbar-field--wide" : ""}`}>
      <label className="map-toolbar-label">{label}</label>
      <Select2Field
        key={`${name}-${toolbarSelectKey}`}
        name={name}
        options={options}
        placeholder={label}
        value={value}
        disabled={disabled}
        onChange={onChange}
        allowClear={allowClear}
        showAvatars={showAvatars}
        loadOptions={loadOptions}
        asyncPageSize={25}
        dropdownParent={typeof document !== "undefined" ? document.body : undefined}
      />
    </div>
  );

  const comparePending =
    Boolean(formik.values.compareDate) &&
    mapContext !== MAP_CONTEXT.TEACHER_TIMELINE &&
    !filteredCompareTeachers.length &&
    !loadingTeachers;

  return (
    <div
      className={`map-page-root${sidePanelOpen ? " map-page-root--panel-open" : ""}${filtersDrawerOpen ? " map-page-root--filters-open" : ""}${mapContext === MAP_CONTEXT.TEACHER_TIMELINE ? " map-page-root--timeline-open" : ""}`}
    >
      <div className="map-top-stack">
      <FormikProvider value={formik}>
      <SoftBox component="form" role="form" onSubmit={formik.handleSubmit}>
        <div className="map-toolbar map-toolbar--distribution">
          <div className="map-toolbar-primary">
            <div className="map-toolbar-row map-toolbar-row--geo">
              {filterField(
                t("assignments.province", "Province"),
                "province",
                [{ id: "", text: t("assignments.province_placeholder", "Province") }, ...provOpts],
                formik.values.province,
                (val) => {
                  if (val) localStorage.setItem(DEFAULT_PROVINCE_STORAGE_KEY, String(val));
                  formik.setFieldValue("province", val);
                  formik.setFieldValue("department", "");
                  formik.setFieldValue("subprefecture", "");
                  formik.setFieldValue("selectschool", "");
                  setDeptOpts([]);
                  setSubpOpts([]);
                  clearMapSelection({ preserveViewport: true });
                  if (val) fetchDepartments(val);
                  fetchEstablishments(
                    { province: val, department: "", subprefecture: "" },
                    val ? { boundsOverride: FULL_WORLD_BOUNDS, fitResults: true } : {}
                  );
                }
              )}

              {filterField(
                t("assignments.department", "Department"),
                "department",
                [{ id: "", text: t("assignments.department_placeholder", "Department") }, ...deptOpts],
                formik.values.department,
                (val) => {
                  formik.setFieldValue("department", val);
                  formik.setFieldValue("subprefecture", "");
                  formik.setFieldValue("selectschool", "");
                  setSubpOpts([]);
                  clearMapSelection({ preserveViewport: true });
                  if (val) fetchSubprefectures(val);
                  fetchEstablishments(
                    { department: val, subprefecture: "" },
                    val ? { boundsOverride: FULL_WORLD_BOUNDS, fitResults: true } : {}
                  );
                },
                !formik.values.province
              )}

              {filterField(
                t("assignments.subprefecture", "Sub-prefecture"),
                "subprefecture",
                [{ id: "", text: t("assignments.subprefecture_placeholder", "Sub-prefecture") }, ...subpOpts],
                formik.values.subprefecture,
                (val) => {
                  formik.setFieldValue("subprefecture", val);
                  formik.setFieldValue("selectschool", "");
                  clearMapSelection({ preserveViewport: true });
                  fetchEstablishments(
                    { subprefecture: val },
                    val ? { boundsOverride: FULL_WORLD_BOUNDS, fitResults: true } : {}
                  );
                },
                !formik.values.department
              )}

              {filterField(
                t("map.select_establishment", "Establishment"),
                "selectschool",
                [
                  { id: "", text: t("map.all_establishments", "All establishments on map") },
                  ...establishments.map((school) => ({ id: school.id, text: school.name })),
                ],
                formik.values.selectschool,
                (val) => {
                  formik.setFieldValue("selectschool", val);
                  formik.setFieldValue("selectTeacher", "");
                  setSelectedTeacherMeta(null);
                  setSelectedTimelineDay("all");
                  if (val) selectEstablishment(val, { openPopup: true, attendDate: formik.values.endDate });
                  else clearMapSelection();
                },
                loading || establishments.length === 0,
                true
              )}
            </div>

            <div className="map-toolbar-row map-toolbar-row--establishment-filters">
              {filterField(
                t("establishments.col.type", "Type"),
                "type",
                typeOptions,
                formik.values.type,
                (val) => handleEstablishmentFilterChange("type", val)
              )}

              {filterField(
                t("establishments.col.status", "Status"),
                "status",
                statusOptions,
                formik.values.status,
                (val) => handleEstablishmentFilterChange("status", val)
              )}
            </div>

            <div className="map-toolbar-row map-toolbar-row--filters">
              {filterField(
                t("map.select_teacher", "Teacher"),
                "selectTeacher",
                [
                  { id: "", text: t("map.all_teachers_at_school", "All teachers at school") },
                  ...teacherRosterOpts,
                ],
                formik.values.selectTeacher,
                (val) => {
                  formik.setFieldValue("selectTeacher", val);
                  if (!val && formik.values.selectschool) {
                    clearTeacherSelection();
                  }
                },
                !formik.values.selectschool,
                true,
                true,
                true,
                loadTeacherRosterOptions
              )}

              <div className="map-toolbar-field map-toolbar-field--date">
                <label className="map-toolbar-label" htmlFor="map-start-date">
                  {t("map.filter.start_date", "From")}
                </label>
                <input
                  id="map-start-date"
                  type="date"
                  name="startDate"
                  value={formik.values.startDate}
                  onChange={(e) => formik.setFieldValue("startDate", e.target.value)}
                  className="map-toolbar-input map-toolbar-native-date"
                />
              </div>

              <div className="map-toolbar-field map-toolbar-field--date">
                <label className="map-toolbar-label" htmlFor="map-end-date">
                  {t("map.filter.end_date", "To")}
                </label>
                <input
                  id="map-end-date"
                  type="date"
                  name="endDate"
                  value={formik.values.endDate}
                  onChange={(e) => formik.setFieldValue("endDate", e.target.value)}
                  className="map-toolbar-input map-toolbar-native-date"
                />
              </div>

              <div className="map-toolbar-field map-toolbar-field--date">
                <label className="map-toolbar-label" htmlFor="map-compare-date-toolbar">
                  {t("map.filter.compare_date", "Compare date")}
                </label>
                <input
                  id="map-compare-date-toolbar"
                  type="date"
                  name="compareDate"
                  value={formik.values.compareDate}
                  onChange={(e) => formik.setFieldValue("compareDate", e.target.value)}
                  className="map-toolbar-input map-toolbar-native-date map-toolbar-native-date--compare"
                />
              </div>

              <div className="map-toolbar-actions">
                <SoftButton
                  type="submit"
                  className="map-toolbar-search"
                  variant="gradient"
                  color="info"
                  disabled={loadingTeachers || establishments.length === 0}
                >
                  {loadingTeachers ? t("map.loading_teachers", "Loading…") : t("home.search", "Search")}
                </SoftButton>
                <button
                  type="button"
                  className={`map-toolbar-more${filtersDrawerOpen ? " map-toolbar-more--open" : ""}`}
                  onClick={() => setFiltersDrawerOpen(true)}
                  aria-expanded={filtersDrawerOpen}
                >
                  <Icon sx={{ fontSize: "1rem !important" }}>tune</Icon>
                  {t("map.filter.show_advanced", "Filters")}
                  {advancedFilterCount > 0 ? (
                    <span className="map-toolbar-more-badge">{advancedFilterCount}</span>
                  ) : null}
                </button>
              </div>
            </div>
          </div>
        </div>
      </SoftBox>
      </FormikProvider>

      {(compareActive || comparePending) && mapContext !== MAP_CONTEXT.TEACHER_TIMELINE && (
        <div className="map-compare-banner" role="status">
          <strong>{t("map.compare.banner_title", "Date comparison")}</strong>
          <span>
            {comparePending
              ? t(
                  "map.compare.banner_pending",
                  "Compare date set — click Search. Markers view only: green/orange = From–To, purple = compare day (dots shifted)."
                )
              : t(
                  "map.compare.banner_active",
                  "Markers view: {{primary}} check-in(s) from From–To (green/orange), {{compare}} on {{date}} (purple, shifted)."
                )
                  .replace("{{primary}}", String(filteredTeachers.length))
                  .replace("{{compare}}", String(filteredCompareTeachers.length))
                  .replace("{{date}}", formik.values.compareDate)}
          </span>
        </div>
      )}

      {deepLinkError ? (
        <div className="map-deeplink-error" role="alert">
          {deepLinkError}
        </div>
      ) : null}
      </div>

      <MapFiltersDrawer
        open={filtersDrawerOpen}
        onClose={() => setFiltersDrawerOpen(false)}
        values={formik.values}
        onChange={handleDrawerFilterChange}
        onReset={resetAdvancedFilters}
        advancedFilterCount={advancedFilterCount}
        genderOptions={genderOptions}
        degreeOptions={degreeOptions}
        contractTypeOptions={contractTypeOptions}
        filteredCount={displayTeachers.length}
        totalCount={rawTeachers.length + compareTeachers.length}
        mapContext={mapContext}
        checkInCount={filteredTeachers.length}
      />

      <div className="map-status-card">
        <>
            {establishmentMapError ? (
              <span className="map-status-chip map-status-chip--warn" role="alert">
                {establishmentMapError}
              </span>
            ) : null}
            <span className="map-status-chip">
              {t("map.establishments_on_map", "{{count}} on map").replace("{{count}}", String(mappableCount))}
            </span>
            {loading && mapLoadProgress.total > 0 ? (
              <span className="map-status-chip map-status-chip--accent" role="status" aria-live="polite">
                {mapLoadProgress.totalInScope > 0
                  ? t(
                      "map.progressive_loading_count",
                      "Loading {{loadedCount}} of {{totalCount}} establishments — zone {{zone}}/{{zones}}"
                    )
                      .replace("{{loadedCount}}", String(mappableCount))
                      .replace("{{totalCount}}", String(mapLoadProgress.totalInScope))
                      .replace("{{zone}}", String(mapLoadProgress.loaded))
                      .replace("{{zones}}", String(mapLoadProgress.total))
                  : t("map.progressive_loading", "Loading map — zone {{zone}}/{{zones}}")
                      .replace("{{zone}}", String(mapLoadProgress.loaded))
                      .replace("{{zones}}", String(mapLoadProgress.total))}
              </span>
            ) : null}
            {!loading && !establishmentMapError && returnedMapFeatures === 0 ? (
              <span className="map-status-chip map-status-chip--warn">
                {t(
                  "map.no_establishments_for_filters",
                  "Aucun établissement ne correspond aux filtres sélectionnés dans cette zone."
                )}
              </span>
            ) : null}
            <span className="map-status-chip map-status-chip--accent">
              {loadingTeachers
                ? t("map.loading_teachers", "Loading teachers…")
                : mapContext === MAP_CONTEXT.TEACHER_TIMELINE && selectedTimelineDay !== "all"
                  ? t("map.teachers_shown_day", "{{count}} on {{day}}")
                      .replace("{{count}}", String(displayTeachers.length))
                      .replace("{{day}}", selectedTimelineDay.slice(5))
                  : t("map.teachers_shown", "{{count}} check-in(s)").replace(
                      "{{count}}",
                      String(filteredTeachers.length)
                    )}
            </span>
            {mapContext === MAP_CONTEXT.TEACHER_TIMELINE && (
              <span className="map-status-chip">{t("map.mode.teacher_timeline", "Teacher timeline")}</span>
            )}
            {mapContext === MAP_CONTEXT.SCHOOL_OVERVIEW && selectedEstabId && (
              <span className="map-status-chip">{t("map.mode.school_overview", "School overview")}</span>
            )}
            {mapContext === MAP_CONTEXT.AREA_OVERVIEW && areaViewActive && (
              <span className="map-status-chip">{t("map.mode.area_overview", "Area overview")}</span>
            )}
            {formik.values.compareDate && filteredCompareTeachers.length > 0 && (
              <span className="map-status-chip map-status-chip--compare">
                {t("map.compare_active", "Compare {{date}}: {{count}} check-in(s)")
                  .replace("{{date}}", formik.values.compareDate.slice(5))
                  .replace("{{count}}", String(filteredCompareTeachers.length))}
              </span>
            )}
            {formik.values.estabHeatLayer && (
              <span className="map-status-chip">{t("map.filter.estab_heat", "Sub-prefecture heat layer")}</span>
            )}
            {teachersRevealReady && (
              <>
                <span className="map-status-chip map-status-chip--good">
                  {t("map.kpi.in_radius", "In radius")}:{" "}
                  {mapContext === MAP_CONTEXT.TEACHER_TIMELINE && selectedTimelineDay !== "all"
                    ? displayRadiusBuckets.inRadius
                    : radiusBuckets.inRadius}
                </span>
                <span className="map-status-chip map-status-chip--warn">
                  {t("map.kpi.out_radius", "Out of radius")}:{" "}
                  {mapContext === MAP_CONTEXT.TEACHER_TIMELINE && selectedTimelineDay !== "all"
                    ? displayRadiusBuckets.outRadius
                    : radiusBuckets.outRadius}
                </span>
              </>
            )}
            {areaSummaryRows.length > 0 && (
              <span className="map-status-chip">
                {t("map.kpi.check_in_rate", "Check-in rate")}: {checkInRate}%
              </span>
            )}
            {(selectedEstabId || areaViewActive) && (
              <button type="button" className="map-status-chip map-status-chip--action" onClick={clearMapSelection}>
                {t("map.clear_selection", "Show all establishments")}
              </button>
            )}
            {formik.values.selectTeacher && selectedEstabId && (
              <button type="button" className="map-status-chip map-status-chip--action" onClick={clearTeacherSelection}>
                {t("map.clear_teacher", "All teachers at school")}
              </button>
            )}
            {areaViewActive && mapContext !== MAP_CONTEXT.AREA_OVERVIEW && (
              <span className="map-status-chip">{t("map.area_view_active", "Area view — all establishments")}</span>
            )}
          </>
      </div>

      {mapContext === MAP_CONTEXT.TEACHER_TIMELINE && (
        <MapTimelineBar
          teacherName={selectedTeacherMeta?.name}
          teacherReg={selectedTeacherMeta?.reg}
          days={timelineDays}
          selectedDay={selectedTimelineDay}
          onSelectDay={setSelectedTimelineDay}
          stats={timelineStats}
          onPrev={handleTimelinePrev}
          onNext={handleTimelineNext}
        />
      )}

      <MapSidePanel
        open={sidePanelOpen}
        onToggle={() => setSidePanelOpen((v) => !v)}
        startDate={summaryStartDate}
        endDate={summaryEndDate}
        daysPresent={summaryDays}
        onStartDateChange={setSummaryStartDate}
        onEndDateChange={setSummaryEndDate}
        onDaysChange={setSummaryDays}
        onLoadSummary={loadAreaSummary}
        loadingSummary={loadingSummary}
        summaryRows={areaSummaryRows}
        onExport={handleExport}
        exportDisabled={!filteredTeachers.length}
        radiusBuckets={radiusBuckets}
        checkInRate={checkInRate}
      />

      {loading && returnedMapFeatures === 0 && (
        <div className="map-loading-overlay">
          <Loading />
        </div>
      )}

      {(loadingTeachers || deepLinkLoading) && (
        <div className="map-teachers-loading-bar" role="status" aria-live="polite">
          <LinearProgress color="info" />
          <span>
            {deepLinkLoading
              ? t("map.deep_link_loading", "Opening teacher on map…")
              : t("map.loading_teachers_progress", "Loading teacher check-ins…")}
          </span>
        </div>
      )}

      <div id="map" className="map-canvas" />
    </div>
  );
}
